import SEO_ROUTES from "@/data/seoRoutes.json";
import { Link } from "react-router-dom";
import { MessageSquareText, ClipboardCheck, PackageCheck, ShieldCheck } from "lucide-react";
import { Seo, breadcrumbSchema, itemListSchema } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { ProductCard } from "@/components/site/ProductCard";
import { Reveal } from "@/components/site/Reveal";
import { Disclaimer } from "@/components/site/Disclaimer";
import { WhatsAppButton } from "@/components/site/WhatsAppButton";
import { PRODUCTS } from "@/data/products";
import { WHATSAPP_MESSAGES } from "@/config/site";

const STEPS = [
  { icon: MessageSquareText, title: "1. Enquire", body: "Choose a product and send an enquiry, or message us on WhatsApp." },
  { icon: ClipboardCheck, title: "2. Confirm details", body: "We confirm availability, sizes, current pricing and label information." },
  { icon: PackageCheck, title: "3. Arrange your order", body: "We agree payment and delivery or collection with you directly." },
];

const crumbs = [
  { name: "Home", path: "/" },
  { name: "Our Products", path: "/products" },
];

export default function Products() {
  return (
    <>
      <Seo
        title={SEO_ROUTES["/products"].title}
        description={SEO_ROUTES["/products"].description}
        path="/products"
        jsonLd={[breadcrumbSchema(crumbs), itemListSchema()]}
      />
      <PageHero
        crumbs={crumbs}
        eyebrow="Shop our products"
        title="Explore Our Natural Wellness Products"
        intro="Browse the Uhlumo Organics range. Each product has its own information page. Orders are currently placed by enquiry — ask us for sizes, label details and pricing."
      >
        <div className="mt-7 flex flex-wrap gap-3">
          <a href="#range" className="btn btn-primary" data-testid="products-hero-browse-button">
            Browse the range
          </a>
          <WhatsAppButton message={WHATSAPP_MESSAGES.pricing} label="Request Product Pricing" className="btn btn-outline" testid="products-hero-pricing-whatsapp" location="products-hero" />
        </div>
      </PageHero>

      <section id="range" className="section scroll-mt-24" aria-labelledby="range-heading">
        <div className="container-site">
          <h2 id="range-heading" className="h-section">
            The Uhlumo Organics range
          </h2>
          <p className="lead mt-2">{PRODUCTS.length} products · prices on request</p>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-7" data-testid="products-grid">
            {PRODUCTS.map((p, i) => (
              <Reveal key={p.slug} delay={i * 60}>
                <ProductCard product={p} ctaLabel="View Details" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section surface-sage" aria-labelledby="how-heading">
        <div className="container-site">
          <h2 id="how-heading" className="h-section">
            How ordering works
          </h2>
          <p className="lead mt-2 max-w-2xl">We do not take online payments yet. Every order starts with a quick conversation.</p>
          <ol className="mt-10 grid gap-6 md:grid-cols-3">
            {STEPS.map(({ icon: Icon, title, body }) => (
              <li key={title} className="card-soft p-7">
                <Icon className="h-6 w-6 text-[color:var(--u-green)]" aria-hidden="true" />
                <h3 className="mt-4 text-xl font-semibold">{title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">{body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="section" aria-labelledby="promise-heading">
        <div className="container-site grid gap-8 lg:grid-cols-2">
          <div className="card-soft p-7 sm:p-9">
            <ShieldCheck className="h-6 w-6 text-[color:var(--u-blue)]" aria-hidden="true" />
            <h2 id="promise-heading" className="mt-4 text-2xl font-semibold">
              Our product information promise
            </h2>
            <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
              We only publish ingredients, sizes, storage and safety details after checking them against actual packaging and supplier information. Where a detail is still being verified, we say so — and we are happy to answer your questions directly.
            </p>
            <Link to="/faqs" className="link-accent mt-5 inline-block text-sm">
              Read our FAQs →
            </Link>
          </div>
          <Disclaimer />
        </div>
      </section>
    </>
  );
}
